import React, { useEffect, useState, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { getFeatured, createFeatured, updateFeatured, deleteFeatured, getVideos } from './api';

export default function FeaturedSpotsPanel() {
  const { token } = useOutletContext();
  const [featured, setFeatured] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectVideo, setSelectVideo] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [feat, vids] = await Promise.all([
        getFeatured(token),
        getVideos(token)
      ]);
      // Ensure featured items are sorted by display_order
      const sortedFeatured = [...feat].sort((a, b) => a.display_order - b.display_order);
      setFeatured(sortedFeatured);
      setVideos(vids);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load featured spots or videos: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { 
    if (token) {
      fetchAll(); 
    }
  }, [token]);

  const handleAdd = async e => {
    e.preventDefault();
    if (!selectVideo) return;
    try {
      await createFeatured({ video_id: selectVideo, display_order: featured.length + 1 }, token);
      setSelectVideo('');
      fetchAll();
    } catch (err) {
      setError('Failed to add featured spot');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this featured spot?')) return;
    try {
      setLoading(true);
      setError('');
      await deleteFeatured(id, token);
      // After successful deletion, refetch all data to ensure consistency
      await fetchAll();
      
    } catch (err) {
      console.error('Error deleting featured spot:', err);
      setError('Failed to delete featured spot: ' + (err.message || 'Unknown error'));
      // Refresh to ensure we're in sync with the server
      await fetchAll();
    } finally {
      setLoading(false);
    }
  };

  const moveSpot = async (currentIndex, direction) => {
    try {
      setLoading(true);
      setError('');
      
      const newIndex = currentIndex + direction;
      if (newIndex < 0 || newIndex >= featured.length) return;

      // Optimistically update the UI
      const updatedItems = [...featured];
      const [movedItem] = updatedItems.splice(currentIndex, 1);
      updatedItems.splice(newIndex, 0, movedItem);
      
      // Update display orders locally
      const withUpdatedOrders = updatedItems.map((item, index) => ({
        ...item,
        display_order: index + 1
      }));
      
      // Optimistically update the local state for a smoother UI experience
      setFeatured(withUpdatedOrders);
      
      // Find the item that was actually moved and its original display_order
      // The `movedItem` from the splice operation still has its original display_order before remapping
      const originalMovedItem = featured[currentIndex]; 
      const itemToUpdate = withUpdatedOrders.find(item => 
        item.featured_spot_id === originalMovedItem.featured_spot_id
      ); // NEW: Use featured_spot_id for matching

      if (itemToUpdate) {
        const updatePayload = {
          video_id: itemToUpdate.video_id, // Usually unchanged during reorder
          display_order: itemToUpdate.display_order, // The new display_order
          old_display_order: originalMovedItem.display_order // The original display_order before moving
        };
        await updateFeatured(itemToUpdate.featured_spot_id, updatePayload, token); // NEW: Pass featured_spot_id
      }
      
      // After successful update, refetch all data to ensure consistency
      await fetchAll();
      
    } catch (err) {
      console.error('Error reordering featured spots:', err);
      setError('Failed to reorder featured spots: ' + (err.message || 'Unknown error'));
      // Refresh to ensure we're in sync with the server
      await fetchAll();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ margin: '32px 0', color: 'white' }}>
      <h3>Featured Spots</h3>
      {loading && <div>Loading...</div>}
      {error && <div style={{ color: 'red', margin: '10px 0' }}>{error}</div>}
      
      <form onSubmit={handleAdd} style={{ marginBottom: '24px' }}>
        <div className="mb-6">
          <select 
            value={selectVideo} 
            onChange={(e) => setSelectVideo(e.target.value)}
            style={{ marginRight: 8, padding: 4, minWidth: '200px' }}
            disabled={loading}
          >
            <option value="">Select a video</option>
            {videos
              .filter(video => !featured.some(f => f.video_id === video.id))
              .map(video => (
                <option key={video.id} value={video.id}>
                  {video.title}
                </option>
              ))}
          </select>
          <button 
            type="submit" 
            disabled={!selectVideo || loading}
            style={{ 
              padding: '4px 8px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Add to Featured
          </button>
        </div>
      </form>

      <div>
        {featured.map((item, index) => (
          <div 
            key={item.featured_spot_id} 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              marginBottom: '10px',
              padding: '10px',
              backgroundColor: 'transparent',
              borderBottom: '1px solid #eee',
              borderRadius: '4px'
            }}
          >
            <div style={{ 
              width: '24px', 
              textAlign: 'center',
              fontWeight: 'bold',
              marginRight: '10px'
            }}>
              {item.display_order}.
            </div>
            <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {videos.find(v => v.id === item.video_id)?.title || `Video ${item.video_id}`}
            </div>
            <div>
              <button 
                type="button"
                onClick={() => moveSpot(index, -1)} 
                disabled={index === 0 || loading}
                style={{ 
                  margin: '0 2px', 
                  padding: '2px 8px',
                  backgroundColor: index === 0 ? '#cccccc' : '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: index === 0 ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1
                }}
                title="Move up"
              >
                ↑
              </button>
              <button 
                type="button"
                onClick={() => moveSpot(index, 1)} 
                disabled={index === featured.length - 1 || loading}
                style={{ 
                  margin: '0 2px', 
                  padding: '2px 8px',
                  backgroundColor: index === featured.length - 1 ? '#cccccc' : '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: index === featured.length - 1 ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1
                }}
                title="Move down"
              >
                ↓
              </button>
              <button 
                type="button"
                onClick={() => handleDelete(item.featured_spot_id)}
                disabled={loading}
                style={{ 
                  marginLeft: '8px', 
                  padding: '2px 8px',
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1
                }}
                title="Remove"
              >
                ×
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
